import { useSendMessage, useSubmitCsat, useThread, useThreads } from '@zuruflex/api-client/react';
import {
  Button,
  cn,
  CsatPrompt,
  EmptyState,
  ErrorState,
  MessageComposer,
  MessageList,
  PageHeader,
  Skeleton,
  ThreadAvatar,
  ThreadContextBar,
  ThreadRow,
} from '@zuruflex/ui';
import { LifeBuoy } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useCsatPrompt, useSupport } from '../lib/support.js';

/**
 * The owner's side of every conversation about their cars.
 *
 * Two panes rather than three: a partner is answering people, not running
 * a queue, so there is no status, no assignee and no filter beyond the
 * list itself. The admin inbox is the one that needs all of that.
 */
export function InboxPage() {
  const { t } = useTranslation(['partner', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  /*
   * The dialog itself lives in `lib/support`, mounted once by the layout,
   * so a booking row and a car page can open it with their own context
   * already filled in. This page is just one more caller with nothing to
   * add.
   */
  const { openSupport } = useSupport();

  const threads = useThreads(api, { perPage: 50 });
  const items = threads.data?.items ?? [];

  /*
   * `?booking=ZF-…` used to be how the booking row's Message button got
   * here, matched against the thread's own booking. That cannot work now
   * that one thread carries every hire a renter has taken — the reference
   * would only ever match whichever one the head happens to be on. The
   * button opens the thread itself and navigates to its id, so all that is
   * left here is picking the first row when nothing is selected.
   */
  const selected = id ?? items[0]?.id;

  useEffect(() => {
    if (selected && selected !== id) navigate(`/inbox/${selected}`, { replace: true });
  }, [selected, id, navigate]);

  if (threads.error) {
    return <ErrorState error={threads.error} onRetry={() => void threads.refetch()} />;
  }

  return (
    /*
      The height of the window, less the main column's own padding, so the
      conversation is a pane that scrolls inside itself rather than a box of
      fixed height floating in the page with the composer wherever 70vh
      happened to land. Below `lg` the panes stack and the page scrolls.
    */
    <div className="flex flex-col gap-6 lg:h-[calc(100dvh-4rem)] lg:min-h-[32rem]">
      <PageHeader
        title={t('inbox.title')}
        description={t('inbox.subtitle')}
        action={
          <Button variant="outline" onClick={() => openSupport()}>
            <LifeBuoy className="size-4" />
            {t('common:support.entryLabel')}
          </Button>
        }
      />

      {threads.isPending ? (
        <Skeleton className="h-96 w-full lg:h-auto lg:flex-1" />
      ) : items.length === 0 ? (
        <EmptyState title={t('inbox.empty')} description={t('inbox.emptyBody')} />
      ) : (
        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[22rem_minmax(0,1fr)]">
          <ul className="flex max-h-[40vh] flex-col gap-0.5 overflow-y-auto rounded-panel border border-border-default bg-surface p-2 lg:max-h-none">
            {items.map((thread) => (
              <li key={thread.id}>
                <ThreadRow
                  thread={thread}
                  active={thread.id === selected}
                  as={(children, className) => (
                    <button
                      type="button"
                      className={className}
                      onClick={() => navigate(`/inbox/${thread.id}`)}
                    >
                      {children}
                    </button>
                  )}
                />
              </li>
            ))}
          </ul>

          <Conversation id={selected} />
        </div>
      )}
    </div>
  );
}

function Conversation({ id }: { id: string | undefined }) {
  const { t } = useTranslation(['partner', 'common']);
  const thread = useThread(api, id);
  const send = useSendMessage(api, id);
  const csat = useSubmitCsat(api, id);
  /*
    Whether to ask how the help went. The rule is `shouldAskCsat`'s, from
    `@zuruflex/core` — support, resolved, asked and not yet answered — and the
    website and both phone apps ask the same question of the same
    function. Called before the returns below, as every hook must be.
  */
  const rate = useCsatPrompt(thread.data);

  const pane = 'h-[75dvh] lg:h-full';

  if (!id) return <EmptyState title={t('inbox.pick')} />;
  if (thread.error) {
    return <ErrorState error={thread.error} onRetry={() => void thread.refetch()} />;
  }
  if (thread.isPending) return <Skeleton className={cn('w-full rounded-panel', pane)} />;

  const data = thread.data;

  return (
    <section
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-panel border border-border-default bg-surface',
        pane,
      )}
    >
      <header className="flex items-center gap-3 border-b border-border-default px-4 py-3 md:px-6">
        <ThreadAvatar thread={data} />
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-text">
            {data.with.kind === 'ZURUFLEX' ? t('common:messaging.zuruflex') : data.with.name}
          </h2>
          <p className="truncate text-xs text-text-muted">
            {data.reference}
            {data.with.phone ? <span> · {data.with.phone}</span> : null}
          </p>
        </div>
      </header>

      {/*
        Which of this renter's hires is being discussed. Under the name
        rather than in it: the conversation is with the person and outlasts
        any one booking, so the subject is a line that moves.
      */}
      <ThreadContextBar thread={data} className="md:px-6" />

      <MessageList
        messages={data.messages}
        readBySeq={data.readBySeq}
        resolveAttachment={(fileId) => api.attachmentLink(fileId).then((link) => link.url)}
        empty={<EmptyState title={t('common:messaging.noMessages')} />}
      />

      {/*
        Between the transcript and the composer, which is where it is on
        the website and on the phones too: after everything that was said,
        and above the box somebody would use to say more. It stays up once
        drawn, so the answer is met by the thank-you rather than by the
        panel vanishing.
      */}
      {rate.visible ? (
        <div className="border-t border-border-default px-4 py-3 md:px-6">
          <CsatPrompt
            csat={data.csat}
            pending={csat.isPending}
            onSubmit={(value) => csat.mutate(value)}
            onDismiss={rate.dismiss}
          />
          {/* A score that silently does nothing is worse than no score.
              `CsatPrompt` has no error slot of its own, so the line goes
              here — exactly as it does on the website. */}
          {csat.isError ? (
            <p className="mt-2 text-xs font-medium text-danger">{t('common:support.error')}</p>
          ) : null}
        </div>
      ) : null}

      <MessageComposer
        masked={data.masksContactDetails}
        pending={send.isPending}
        onAttach={(file) => api.uploadThreadAttachment(id, file)}
        onSend={async (body, { attachments }) => {
          await send.mutateAsync({ body, attachments, idempotencyKey: crypto.randomUUID() });
        }}
      />
    </section>
  );
}
